import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Undo2, Check, ArrowLeft, Clock, Loader2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color mapping for stat buttons
const getStatColor = (colorClass) => {
  const colorMap = {
    'bg-green-500': 'bg-green-500 hover:bg-green-600',
    'bg-red-500': 'bg-red-500 hover:bg-red-600',
    'bg-red-400': 'bg-red-400 hover:bg-red-500',
    'bg-red-600': 'bg-red-600 hover:bg-red-700',
    'bg-blue-500': 'bg-blue-500 hover:bg-blue-600',
    'bg-yellow-500': 'bg-yellow-500 hover:bg-yellow-600 text-slate-900',
    'bg-purple-500': 'bg-purple-500 hover:bg-purple-600',
    'bg-orange-500': 'bg-orange-500 hover:bg-orange-600',
    'bg-cyan-500': 'bg-cyan-500 hover:bg-cyan-600',
    'bg-indigo-500': 'bg-indigo-500 hover:bg-indigo-600',
    'bg-pink-500': 'bg-pink-500 hover:bg-pink-600',
    'bg-emerald-500': 'bg-emerald-500 hover:bg-emerald-600',
    'bg-amber-500': 'bg-amber-500 hover:bg-amber-600 text-slate-900',
    'bg-teal-500': 'bg-teal-500 hover:bg-teal-600',
    'bg-lime-500': 'bg-lime-500 hover:bg-lime-600 text-slate-900',
  };
  return colorMap[colorClass] || 'bg-slate-500 hover:bg-slate-600';
};

// Outcome Modal Component
const OutcomeModal = ({ stat, onSelect, onClose, stats }) => {
  if (!stat || !stat.outcomes) return null;
  
  // Calculate total for this stat group
  const totalCount = stat.outcomes.reduce((sum, outcome) => 
    sum + (stats[outcome.key] || 0), 0
  );

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-2xl p-6 max-w-sm w-full border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{stat.icon}</span>
            <div>
              <h3 className="text-xl font-bold text-white">{stat.label}</h3>
              <p className="text-slate-400 text-sm">Total: {totalCount}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-700 transition-colors"
            data-testid="outcome-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Outcome Buttons */}
        <div className="grid gap-3">
          {stat.outcomes.map((outcome) => (
            <button
              key={outcome.key}
              onClick={() => {
                onSelect(outcome.key);
                onClose();
              }}
              className={`${getStatColor(outcome.color)} rounded-xl p-4 text-white font-semibold 
                transition-all duration-150 active:scale-95 shadow-lg flex items-center justify-between`}
              data-testid={`outcome-${outcome.key}`}
            >
              <span className="text-lg">{outcome.label}</span>
              <span className="text-2xl font-bold bg-white/20 px-3 py-1 rounded-lg">
                {stats[outcome.key] || 0}
              </span>
            </button>
          ))}
        </div>
        
        {/* Cancel */}
        <button
          onClick={onClose}
          className="w-full mt-4 p-3 rounded-xl bg-slate-700 text-slate-300 hover:bg-slate-600 
            transition-colors font-medium"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const TallyDashboard = ({ session, onComplete, onBack }) => {
  const [stats, setStats] = useState(session.stats || {});
  const [lastEvent, setLastEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [eventCount, setEventCount] = useState(session.stat_events?.length || 0);
  const [positionStats, setPositionStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [outcomeModal, setOutcomeModal] = useState(null); // Stat being selected

  // Fetch sport-specific stats from the API
  useEffect(() => {
    const fetchPositionStats = async () => {
      setLoadingStats(true);
      try {
        const sport = session.sport || 'football';
        const response = await axios.get(`${API}/game-stats/positions?sport=${sport}`);
        const statsData = response.data.stats_by_position || {};
        const posStats = statsData[session.position] || [];
        setPositionStats(posStats);
      } catch (error) {
        setPositionStats([]);
        toast.error('Failed to load position stats');
      } finally {
        setLoadingStats(false);
      }
    };
    
    fetchPositionStats();
  }, [session.sport, session.position]);

  const handleTally = useCallback(async (statKey) => {
    // Optimistic update - functional form so rapid taps can't race on a stale count
    setStats(prev => ({ ...prev, [statKey]: (prev[statKey] || 0) + 1 }));
    setEventCount(prev => prev + 1);

    try {
      const response = await axios.post(`${API}/game-sessions/${session.id}/stats`, {
        stat_key: statKey,
        value: 1
      });
      setLastEvent({ statKey, eventId: response.data.event_id });
    } catch (error) {
      // Revert on error
      setStats(prev => ({ ...prev, [statKey]: Math.max(0, (prev[statKey] || 0) - 1) }));
      setEventCount(prev => Math.max(0, prev - 1));
      toast.error(error.response?.data?.detail || 'Failed to log stat');
    }
  }, [session.id]);

  const handleStatClick = (stat) => {
    if (stat.type === 'outcome' && stat.outcomes) {
      // Show outcome modal
      setOutcomeModal(stat);
    } else {
      // Simple stat - direct tally
      handleTally(stat.key);
    }
  };

  const getStatLabel = (statKey) => {
    for (const stat of positionStats) {
      if (stat.key === statKey) return stat.label;
      const outcome = (stat.outcomes || []).find(o => o.key === statKey);
      if (outcome) return `${stat.label} - ${outcome.label}`;
    }
    return statKey.replace(/_/g, ' ');
  };

  const handleUndo = async () => {
    if (eventCount === 0) {
      toast.error('Nothing to undo');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete(`${API}/game-sessions/${session.id}/stats/undo`);
      const undoneKey = response.data.undone_stat;
      setStats(prev => ({
        ...prev,
        [undoneKey]: Math.max(0, (prev[undoneKey] || 0) - 1)
      }));
      setEventCount(prev => Math.max(0, prev - 1));
      setLastEvent(null);
      toast.success(`Undid: ${getStatLabel(undoneKey)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await axios.post(`${API}/game-sessions/${session.id}/complete`);
      toast.success(`Session complete! You earned ${response.data.points_earned} points!`);
      onComplete({
        ...response.data,
        stats: stats,
        session: {
          ...session,
          stats: stats
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete session');
    } finally {
      setCompleting(false);
    }
  };

  const formatPosition = (pos) => {
    return pos.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Calculate display count for a stat (sum of outcomes if outcome type)
  const getStatDisplayCount = (stat) => {
    if (stat.type === 'outcome' && stat.outcomes) {
      return stat.outcomes.reduce((sum, outcome) => sum + (stats[outcome.key] || 0), 0);
    }
    return stats[stat.key] || 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 pb-24">
      {/* Outcome Modal */}
      {outcomeModal && (
        <OutcomeModal
          stat={outcomeModal}
          stats={stats}
          onSelect={handleTally}
          onClose={() => setOutcomeModal(null)}
        />
      )}

      {/* Header */}
      <div className="max-w-2xl mx-auto mb-4">
        <Button
          variant="ghost"
          className="text-slate-400 hover:text-white mb-2"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="bg-slate-800/90 border-slate-700">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">
                  vs. {session.opponent_name}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  <Badge variant="outline" className="text-slate-300 border-slate-600">
                    {formatPosition(session.position)}
                  </Badge>
                  <span className="text-slate-400 text-sm flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {session.game_date}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-amber-400">
                  {eventCount}
                </div>
                <div className="text-xs text-slate-400">plays logged</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tally Grid */}
      <div className="max-w-2xl mx-auto">
        {loadingStats ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-amber-400 animate-spin mb-4" />
            <p className="text-slate-400">Loading stats for {session.position?.replace(/_/g, ' ')}...</p>
          </div>
        ) : positionStats.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-400">No stats configured for this position</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {positionStats.map((stat) => (
              <button
                key={stat.key}
                onClick={() => handleStatClick(stat)}
                className={`${getStatColor(stat.color)} rounded-xl p-4 text-white font-semibold 
                  transition-all duration-150 active:scale-95 shadow-lg relative`}
                data-testid={`tally-${stat.key}`}
              >
                {/* Outcome indicator */}
                {stat.type === 'outcome' && (
                  <div className="absolute top-2 right-2 bg-white/20 rounded-full px-2 py-0.5 text-xs">
                    tap for options
                  </div>
                )}
                <div className="text-3xl mb-1">{stat.icon}</div>
                <div className="text-lg">{stat.label}</div>
                <div className="text-3xl font-bold mt-1">
                  {getStatDisplayCount(stat)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Floating Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-slate-900 via-slate-900/95 to-transparent">
        <div className="max-w-2xl mx-auto flex gap-3">
          <Button
            variant="outline"
            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
            onClick={handleUndo}
            disabled={loading || eventCount === 0}
            data-testid="undo-last-btn"
          >
            <Undo2 className="w-4 h-4 mr-2" />
            Undo Last
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
            onClick={handleComplete}
            disabled={completing || eventCount === 0}
            data-testid="finish-session-btn"
          >
            {completing ? 'Completing...' : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Finish Session
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TallyDashboard;
